import { useCallback, useContext, useEffect, useRef, useState } from "react"
import _ from "lodash"

import { DateTime } from "luxon"

import {
  GET_ORGANIZATIONS,
  GET_PARTICIPANT,
  GET_STAFF,
} from "./notificationsQueries"

//Helpers
import {
  LogDebug,
  GetLogLevelKey,
  LogError,
  LogWarning,
} from "../../helpers/logger"

//SignalR
import { useClientMethod, useHub } from "react-use-signalr"
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"

//Notifications modules
import {
  noteApiCountPost,
  noteApiListMutedPost,
  noteApiMarkAsReadPut,
} from "./notificationsApi"
import NotificationsToastContent from "./notificationsToastContent"
import NotificationsForm from "./form/notificationsForm"
import {
  notificationHasTags,
  replaceNotificationTags,
} from "./notificationsTags"

//Tasks modules
import TasksForm from "./tasksForm/TasksForm"

// Context
import { UserContext } from "../../context/userContext"

//PrimeReact
import { Badge } from "primereact/badge"
import { Toast } from "primereact/toast"
import { Dialog } from "primereact/dialog"
import { TabMenu } from "primereact/tabmenu"

import { notificationsUrl } from "../../config/microservicesUrls"
import ROUTES from "../Routes/routesContants"
import { useNavigate } from "react-router-dom"

//styles
import noteStyle from "../../styles/notifications/NotificationsForm.module.css"
import { useLazyQuery } from "@apollo/client"
import { handleParticipantNavigateEvent } from "./handlers/participantEventsHandler"

export default function Notifications() {
  const userCtx = useContext(UserContext)

  const [notificationHubConnection, setNotificationHubConnection] =
    useState(null)
  const [notificationsCount, setNotificationsCount] = useState(null)
  const [mutedNotifications, setMutedNotifications] = useState(null)
  const [errorState, setErrorState] = useState(null)
  const [refreshCount, setRefreshCount] = useState(new Date().getTime())
  const [notificationsActiveKey, setNotificationsActiveKey] =
    useState("notifications")
  const [externalAction, setExternalAction] = useState(undefined)

  //organizations
  const [organizationsCache, setOrganizationsCache] = useState([
    {
      organization_id: userCtx.session.user.organization_id,
      organization_alias: userCtx.session.user.organization_alias,
      organization_name: userCtx.session.user.organization_name,
      enterprise_id: userCtx.session.user.enterprise_id,
    },
  ])

  //task related state items
  const [tasksActiveKey, setTasksActiveKey] = useState("assigned-tasks")
  const [tasksActiveSubKey, setTasksActiveSubKey] = useState(0)
  const [taskUsers, setTaskUsers] = useState([])
  const [skipTaskUsersRefresh, setSkipTaskUsersRefresh] = useState(false)

  const navigate = useNavigate()

  const toast = useRef(null)
  const systemNotificationsFilter = ["Staff", "All"]
  const scheduledAssessmentActionUrl = "#ScheduledAssessment"

  // SignalR
  const isReconnecting = useRef(false)

  const { hubConnectionState, error: hubError } = useHub(
    notificationHubConnection,
  )
  useClientMethod(
    notificationHubConnection,
    "NotificationEvent",
    (notification) => {
      handleNotificationEvent(notification)
    },
  )

  function setupSignalRConnection() {
    try {
      const userDetail = `[STAFF] ${userCtx.session.user.staff_id} - ${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`

      //Set isReconnecting to false, because we will be creating a new connection now.
      isReconnecting.current = false

      // Setup Connection
      const connection = new HubConnectionBuilder()
        .withUrl(`${notificationsUrl()}/hubs/notifications?user=${userDetail}`)
        .withAutomaticReconnect()
        .configureLogging(
          GetLogLevelKey() === "debug" ? LogLevel.Debug : LogLevel.Warning,
        )
        .build()

      connection.onreconnecting((error) => {
        LogWarning(`Notification hub reconnecting to server`, error)
        isReconnecting.current = true
      })

      connection.onreconnected(() => {
        // Update notification counter
        getNotificationsCount(false)
        isReconnecting.current = false
      })

      connection.onclose((error) => {
        if (isReconnecting.current || error) {
          const errorMessage = `Notifications have been disconnected, please refresh your browser to reconnect`
          setErrorState(errorMessage)
          //If the connection closed due to an error, attempt to reconnect
          LogWarning(
            errorMessage,
            isReconnecting.current
              ? "Unable to reconnect to the Notifications Hub"
              : error,
          )
          //restartNotificationHub(connection)
        }
      })

      setNotificationHubConnection(connection) // Save connection to state
    } catch (error) {
      setNotificationHubConnection(undefined)
      LogError(
        "Unable to connect to to Notification Hub, retrying in 10 seconds",
        error,
      )
      restartNotificationHub()
    }
  }

  useEffect(() => {
    //if user.show_notifications_settings is true, then show the notifications settings tab
    if (userCtx.showNotificationsSettings) {
      setNotificationsActiveKey("mute")
      userCtx.setNotificationShowState("notifications")
      userCtx.setShowNotificationsSettings(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.showNotificationsSettings])

  function restartNotificationHub(connection, timeOutPeriod) {
    setTimeout(function () {
      if (connection) {
        connection
          .start()
          .then((e) => {
            setErrorState(null)
            getNotificationsCount(true)
          })
          .catch((error) => {
            LogError("Unable to connect to the Notifications hub", error)
            showError(
              "Unable to connect to the Notifications hub. Please refresh the page.",
            )
            restartNotificationHub(connection, 30000) //Retry after 30s
          })
      } else {
        setupSignalRConnection()
      }
    }, timeOutPeriod || 10000) // Restart connection after 10 seconds or as long as requested.
  }

  function notificationToUser(notification) {
    //Check by UserId
    if (notification?.userId) {
      return (
        notification.userId.toString() ===
          userCtx.session.user.staff_id.toString() ||
        notification.userId.toString() ===
          userCtx.session.user.staff_alias.toString()
      )
    }
    // Check by StaffId
    else if (notification?.staffId) {
      return (
        notification.staffId.toString() ===
          userCtx.session.user.staff_id.toString() ||
        notification.staffId.toString() ===
          userCtx.session.user.staff_alias.toString()
      )
    }
    // Check by CaseId (Participant)
    else if (notification?.caseId) {
      return (
        notification.caseId.toString() ===
          userCtx.session.user.staff_id.toString() ||
        notification.caseId.toString() ===
          userCtx.session.user.staff_alias.toString()
      )
    }
  }

  function systemNotificationToUserGroup(notification) {
    return (
      notification.groupId.toString() ===
        userCtx.session.user.organization_id.toString() &&
      notification.systemNotification === true &&
      systemNotificationsFilter.some(
        (filter) => filter === notification.systemNotificationFilter,
      )
    )
  }

  function systemNotificationToUserEnterprise(notification) {
    return (
      notification.enterpriseId?.toString() ===
        userCtx.session.user.enterprise_id.toString() &&
      notification.systemNotification === true &&
      systemNotificationsFilter.some(
        (filter) => filter === notification.systemNotificationFilter,
      )
    )
  }

  async function checkNotificationSoundMuted(eventName) {
    try {
      let _mutedNotifications = !mutedNotifications
        ? await getMutedNotifications()
        : mutedNotifications

      if (_mutedNotifications?.mutedEventTypes?.length > 0) {
        const eventType = _mutedNotifications?.mutedEventTypes.find(
          (x) => x.name === eventName,
        )

        if (eventType && eventType.sound) {
          return true
        }
      }
    } catch (error) {
      console.log(error)
    }

    return false
  }

  async function checkNotificationMuted(eventName) {
    try {
      let _mutedNotifications = !mutedNotifications
        ? await getMutedNotifications()
        : mutedNotifications

      if (_mutedNotifications?.mutedEventTypes?.length > 0) {
        const eventType = _mutedNotifications?.mutedEventTypes.find(
          (x) => x.name === eventName,
        )

        if (eventType && eventType.channelSignalR) {
          return true
        }
      }
    } catch (error) {
      console.log(error)
    }

    return false
  }

  async function getMutedNotifications() {
    try {
      const res = await noteApiListMutedPost(
        userCtx.tokens.getNotificationToken(),
        userCtx.session.user.organization_id,
        userCtx.session.user.staff_id,
      )

      if (res) {
        setMutedNotifications(res)
        return res
      }
    } catch (error) {
      console.error(`Unable to get notification mute settings`, error)
      toast.error((t) => (
        <NotificationsToastContent toast={t} systemNotification>
          Unable to get notification mute settings. See console for details.
        </NotificationsToastContent>
      ))

      return []
    }
  }

  const [getParticipant] = useLazyQuery(GET_PARTICIPANT, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: undefined,
      participant_id: undefined,
      organization_id: undefined,
    },
  })

  const [getStaff] = useLazyQuery(GET_STAFF, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: undefined,
      organization_id: undefined,
    },
  })

  const [getOrgs] = useLazyQuery(GET_ORGANIZATIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
    },
  })

  const loadOrganizations = useCallback(async () => {
    const orgs = await getOrgs()

    if (orgs.data?.organizationsByUid) {
      setOrganizationsCache(orgs.data.organizationsByUid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session?.organizations])

  async function findParticipant(participant_id, organization_id) {
    let result = undefined
    const participant = await getParticipant({
      variables: {
        staff_id: userCtx.session.user.staff_id,
        participant_id: participant_id,
        organization_id: organization_id,
        participant_alias: participant_id,
      },
    })

    if (!!participant.data?.participant) {
      result = participant.data.participant
    } else if (!!participant.data?.search) {
      const searchResult = participant.data.search.filter(
        (result) => result.participant_alias === participant_id,
      )
      if (searchResult.length > 0) {
        result = searchResult[0]
      }
    }

    return result
  }

  async function findStaff(staff_id) {
    let result = undefined
    const staff = await getStaff({
      variables: {
        staff_id: staff_id,
      },
    })

    if (!!staff.data?.user && staff.data?.user.length > 0) {
      result = staff.data.user[0]
    }

    return result
  }

  async function handleNotificationEvent(notification) {
    LogDebug("Notification Event Received", notification)

    // Filter events to active group and user
    const noteToUser = notificationToUser(notification)
    const sysNotificationToUserGroup =
      systemNotificationToUserGroup(notification)
    const sysNotificationToUserEnterprise =
      systemNotificationToUserEnterprise(notification)

    if (
      noteToUser ||
      sysNotificationToUserGroup ||
      sysNotificationToUserEnterprise
    ) {
      const notificationMuted = await checkNotificationMuted(
        notification.eventType,
      )
      if (notificationMuted) LogDebug("Notification Event Muted", notification)

      if (
        (notification.highPriority || notification.showToast) &&
        !notificationMuted
      ) {
        //Check description for system tags
        if (notificationHasTags(notification)) {
          notification = await replaceNotificationTags(
            notification,
            undefined,
            undefined,
            findParticipant,
            undefined,
            findStaff,
          )
        }

        const notificationSoundMuted = await checkNotificationSoundMuted(
          notification.eventType,
        )

        // Show toast message
        const id = Date.now()
        toast.current.show({
          severity: "info",
          detail: id,
          closable: false,
          life:
            notification.primaryActionUrl || notification.secondaryActionUrl
              ? 30000
              : 15000,
          content: (
            <NotificationsToastContent
              toast={toast}
              toastId={id}
              notification={notification}
              notificationId={notification.id}
              systemNotification={notification.systemNotification}
              title={notification.title}
              description={notification.description}
              isReactMessage={notification?.isReactMessage}
              primaryActionLabel={notification.primaryActionLabel}
              primaryActionUrl={notification.primaryActionUrl}
              secondaryActionLabel={notification.secondaryActionLabel}
              secondaryActionUrl={notification.secondaryActionUrl}
              onHandleActionURL={handleActionURL}
              markNotificationRead={markAsRead}
              getOrganizationName={getOrganizationName}
              muteSound={notificationSoundMuted}
            >
              <div>
                <div className="mb-2">
                  <b>{notification.title}</b>
                </div>
                {notification.description}
              </div>
            </NotificationsToastContent>
          ),
        })
      }

      // Update notification counter
      getNotificationsCount()
    }
  }

  const handleShowTasks = (eventKey, eventSubKey) => {
    if (eventKey) {
      setTimeout(() => {
        setTasksActiveKey(eventKey || "assigned-tasks")
        setTasksActiveSubKey(eventSubKey || 0)
        LogDebug("setTasksActiveKey", eventKey)
      }, 500)
    }
    userCtx.setNotificationShowState("tasks")
  }

  const handleShowNotifications = (eventKey) => {
    setNotificationsActiveKey(eventKey || "notifications")
    userCtx.setNotificationShowState("notifications")
  }

  const handleActionURL = async (
    actionUrl,
    notification,
    notificationId,
    executedFromNotifList,
  ) => {
    if (userCtx.session.user.organization_id !== notification.groupId) {
      await userCtx.changeOrganization(notification.groupId)
    }

    if (notification && !notification.read) await markAsRead(notificationId)

    switch (actionUrl) {
      //Assessments actions
      case scheduledAssessmentActionUrl:
        await handleScheduledAssessment(notification)
        userCtx.setNotificationShowState("")
        break
      //Notification actions
      case "#ShowNotifications":
        userCtx.setNotificationShowState("notifications")
        break
      case "#ShowNotificationSettings":
        setNotificationsActiveKey("mute")
        userCtx.setNotificationShowState("notifications")
        break
      case "#mute":
        handleShowNotifications(actionUrl?.replace("#", ""))
        break
      case "#notifications":
      case "#sys-notifications":
        window.location.href = actionUrl
        break
      //Task actions
      case "#assigned-tasks":
        handleShowTasks("assigned-tasks")
        break
      case "#created-tasks-pending":
        handleShowTasks("created-tasks", 0)
        break
      case "#created-tasks-completed":
        handleShowTasks("created-tasks", 1)
        break
      //Document signing actions
      case "#signature-request-sent":
      case "#signature-request-signed":
      case "#signature-request-all-signed":
      case "#signature-request-declined":
      case "#signature-request-canceled":
        userCtx.showDocumentRequests()
        break
      //Telerecovery actions
      case "#accept-call":
        handleIncomingCall(notification, executedFromNotifList)
        userCtx.setNotificationShowState("")
        break
      case "#callback-call":
        handleMissedCall(notification)
        userCtx.setNotificationShowState("")
        break
      case "#go-to-smsconv":
        handleIncomingSMS(notification)
        userCtx.setNotificationShowState("")
        break
      case "#go-to-chatconv":
        handleIncomingChat(notification)
        userCtx.setNotificationShowState("")
        break
      //Referrals actions
      case "#GoToReferral":
        handleReferralNavigation(notification)
        userCtx.setNotificationShowState("")
        break
      case "#GoToPartnerProfile":
        handlePartnerProfileNavigation(notification)
        userCtx.setNotificationShowState("")
        break
      //GPRA actions
      case "#view-gpra":
        handleGpraNavigation(notification)
        break
      //Housing actions
      case "#showHousing":
        handleShowHousing(notification)
        break
      //Participant actions
      case "#ParticipantNavigate":
      case "#ParticipantNavigate1":
      case "#ParticipantNavigate2":
        await handleParticipantNavigateEvent(
          actionUrl,
          notification,
          userCtx,
          navigate,
        )
        userCtx.setNotificationShowState("")
        break
      //2FA actions
      case "#GoTo2FA":
        userCtx.setNotificationShowState("")
        navigate(ROUTES.PROFILE_2FA, {
          replace: true,
        })
        break
      default:
        window.location.href = actionUrl
    }
  }

  if (!!externalAction) {
    const action = externalAction
    setExternalAction(undefined)
    if (!action.actionUrl) {
      LogError("Unable to handle action, no action specified")
    } else {
      handleActionURL(
        action.actionUrl,
        {
          primaryActionUrl: action.actionUrl,
          primaryActionPayload: action.payload,
        },
        undefined,
      )
    }
  }

  /* Assessments specific */
  async function handleScheduledAssessment(notification) {
    try {
      let payload = "{}"
      if (notification?.primaryActionUrl === scheduledAssessmentActionUrl) {
        payload = notification?.primaryActionPayload
      } else if (
        notification?.secondaryActionUrl === scheduledAssessmentActionUrl
      ) {
        payload = notification?.secondaryActionPayload
      }

      if (payload !== "{}")
        if (payload) {
          let cleanPayload = payload
            .replaceAll(`"`, "")
            .replaceAll("\\u0022", `"`)
            .replaceAll("\\", "")

          if (
            cleanPayload[0] === `"` &&
            cleanPayload[cleanPayload.length - 1] === `"`
          ) {
            cleanPayload = cleanPayload.substring(1, cleanPayload.length - 1)
          }

          const shaPayload = JSON.parse(cleanPayload)

          if (!shaPayload?.participant_id) {
            userCtx.setNotificationError(
              "Scheduled assessment: malformed payload, no participant specified",
            )
          } else {
            if (userCtx.participant_id !== shaPayload.participant_id) {
              await userCtx.changeParticipant(shaPayload.participant_id)
            }
            navigate(ROUTES.PARTICIPANT_ASSESSMENTS, {
              replace: true,
              state: {
                taskId: shaPayload?.taskId,
                participant_id: shaPayload.participant_id,
              },
            })
          }
        } else {
          navigate(ROUTES.PARTICIPANT_ASSESSMENTS, {
            replace: true,
            state: { page: "schedule" },
          })
        }
    } catch (error) {
      LogError("unable to process scheduled assessment action", error)
      showError("Unable to process scheduled assessment action")
    }
  }

  /* Referrals specific */
  const handleReferralNavigation = (notification) => {
    if (!!notification.primaryActionPayload) {
      const partnerRoles = [9, 10]
      const payload = notification.primaryActionPayload
        .replaceAll('"', "")
        .replace("\\u0026", "&")
      const editReferralLink = partnerRoles.some(
        (role) => role === userCtx.session.user.staff_role,
      )
        ? ROUTES.REFERRAL_EDIT
        : ROUTES.PARTICIPANT_REFERRAL_EDIT
      const path = `${editReferralLink.substring(
        0,
        editReferralLink.lastIndexOf("/"),
      )}/${payload}`

      navigate(path, {
        replace: true,
      })
    }
  }

  const handlePartnerProfileNavigation = (notification) => {
    if (!!notification.primaryActionPayload) {
      const profileId = notification.primaryActionPayload.replaceAll('"', "")
      const isPartner = [9, 10].includes(userCtx.session.user.staff_role)
      const profileLink = isPartner
        ? ROUTES.PARTNER_ENTITY_PROFILE
        : ROUTES.REFERRAL_NETWORKS

      navigate(profileLink, { replace: true, state: { profileId: profileId } })
    }
  }

  /* GPRA specific */
  const handleGpraNavigation = async (notification) => {
    if (!!notification.caseId) {
      await userCtx.changeParticipant(notification.caseId) // changeParcipant receive participant_id or null
      userCtx.setNotificationShowState("")
      return navigate(ROUTES.PARTICIPANT_GPRA_FORMS, {
        replace: true,
      })
    } else {
      showError("No participant information available to navigate to")
    }
  }

  /* Housing specific */
  const handleShowHousing = async (notification) => {
    if (!!notification.primaryActionPayload) {
      var payloadObject = JSON.parse(
        notification.primaryActionPayload
          .replaceAll(`"`, "")
          .replaceAll("\\u0022", `"`)
          .replaceAll("\\", ""),
      )
      userCtx.housing.setHousing({ showHousing: true, ...payloadObject })
      userCtx.setNotificationShowState("")
    }
  }

  /* Telerecovery specific */

  async function handleMissedCall(notification) {
    await userCtx.changeParticipant(notification.caseId)

    return navigate(ROUTES.PARTICIPANT_TELERECOVERY_SERVICES + "/audio", {
      replace: true,
    })
  }

  async function handleIncomingCall(notification, executedFromNotifList) {
    if (executedFromNotifList && executedFromNotifList === true) {
      return handleMissedCall(notification)
    }

    await userCtx.changeParticipant(notification.caseId)

    const notif_payload = JSON.parse(notification.primaryActionPayload)

    userCtx.setIncomingCallPickedUp("NO")
    userCtx.setIncomingCallQueueId(notif_payload.QueueName)
    userCtx.setIncomingCallEventId(notif_payload.EventId)

    return navigate(ROUTES.PARTICIPANT_TELERECOVERY_SERVICES + "/audio", {
      replace: true,
    })
  }

  async function handleIncomingSMS(notification) {
    await userCtx.changeParticipant(notification.caseId)
    return navigate(ROUTES.PARTICIPANT_TELERECOVERY_SERVICES + "/sms")
  }

  async function handleIncomingChat(notification) {
    await userCtx.changeParticipant(notification.caseId)
    return navigate(ROUTES.PARTICIPANT_TELERECOVERY_SERVICES + "/chat")
  }

  /*Telerecovery specific*/

  const showError = useCallback(
    (message) => {
      const id = Date.now()
      if (toast.current) {
        toast.current.show({
          severity: "error",
          detail: id,
          closable: false,
          sticky: isReconnecting.current,
          content: (
            <NotificationsToastContent
              toast={toast}
              systemNotification
              isError
              toastId={id}
              description={message || errorState}
              isInfo={true}
            />
          ),
        })
      }
    },
    [errorState],
  )

  const showInfo = useCallback((message) => {
    const id = Date.now()

    //If the received message is a string, or does not have a message field, process it normally
    if (
      typeof message === "string" ||
      message instanceof String ||
      !message.message
    ) {
      toast.current.show({
        severity: "info",
        detail: id,
        closable: false,
        content: (
          <NotificationsToastContent
            toast={toast}
            getOrganizationName={getOrganizationName}
            systemNotification
            toastId={id}
            description={message}
            isInfo={true}
          />
        ),
      })
    } else {
      const messageValue = message.message
      const toastConfig = { ...message, message: undefined }

      toast.current.show({
        ...toastConfig,
        severity: "info",
        detail: id,
        closable: false,
        content: (
          <NotificationsToastContent
            toast={toast}
            getOrganizationName={getOrganizationName}
            systemNotification
            toastId={id}
            description={messageValue}
            isReactMessage={message.isReactMessage}
            isInfo={true}
          />
        ),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleButtonClick = () => {
    if (errorState) {
      showError()
    } else {
      handleShowNotifications()
    }
  }

  async function markAsRead(notificationId) {
    if (notificationId) {
      try {
        await noteApiMarkAsReadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
        )
        getNotificationsCount()
        LogDebug(`Notification marked as read (ID: ${notificationId})`)
      } catch (error) {
        console.error(
          `Unable to mark notification as read (id: ${notificationId})`,
          error,
        )
        toast.error((t) => (
          <notificationsToastContent toast={t} systemNotification>
            {`Unable to mark notification as read (id: ${notificationId}). See console for details.`}
          </notificationsToastContent>
        ))
      }
    }
  }

  const getNotificationsCount = useCallback(
    async (notifyNewCount) => {
      try {
        let organization_id = "All"
        let enterprise_id = "All"

        const organizationFilter = organizationsCache
          ? [
              ...organizationsCache.map((org) => org.organization_id),
              ...organizationsCache.map((org) => org.organization_alias),
            ]
          : userCtx.session.organizations.map((org) => org.organization_id)

        if (organization_id && enterprise_id) {
          const res = await noteApiCountPost(
            userCtx.tokens.getNotificationToken(),
            enterprise_id,
            userCtx.session.user.enterprise_alias,
            organization_id,
            userCtx.session.user.organization_alias,
            organizationFilter,
            userCtx.session.user.staff_id,
            userCtx.session.user.staff_alias,
            userCtx.session?.user?.staff_create_date
              ? DateTime.fromISO(
                  userCtx.session.user.staff_create_date,
                ).toJSDate()
              : undefined,
          )

          if (res) {
            if (
              notifyNewCount &&
              res?.numTotal !== notificationsCount?.numTotal &&
              res?.numTotal > 0
            ) {
              showInfo(
                "New notifications received, please open the Notifications Hub for more information",
              )
            }

            setNotificationsCount(res)
            setErrorState(null)
          }
        } else {
          console.warn("No organization or enterprise specified")
        }
      } catch (error) {
        console.error("Unable to count notifications", error)
        setErrorState("Unable to count notifications. See console for details.")
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userCtx.session.user.organization_id,
      userCtx.session.user.enterprise_id,
      userCtx.session.user.staff_role,
      userCtx.session.user.enterprise_alias,
      userCtx.session.user.organization_alias,
      userCtx.session.user.staff_id,
      userCtx.session.user.staff_alias,
      userCtx.session.user.staff_create_date,
      organizationsCache,
    ],
  )

  useEffect(() => {
    const notificationsShown = userCtx.notification_show_state !== ""

    if (!notificationsShown) {
      userCtx.setNotificationsClosed(new Date())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.notification_show_state])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  useEffect(() => {
    getNotificationsCount()
  }, [
    userCtx.session.user.organization_id,
    userCtx.session.user.enterprise_id,
    getNotificationsCount,
  ])

  useEffect(() => {
    if (notificationsCount !== null) {
      getNotificationsCount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCount])

  useEffect(() => {
    if (errorState) {
      showError()
    }
  }, [errorState, showError])

  useEffect(() => {
    if (!!userCtx.notification_error) {
      showError(userCtx.notification_error)
      userCtx.setNotificationError(undefined)
    }
    if (!!userCtx.notification_action) {
      setExternalAction(userCtx.notification_action)
      userCtx.setNotificationAction(undefined)
    }
    if (!!userCtx.notification_refresh) {
      getNotificationsCount()
      userCtx.setNotificationRefresh(undefined)
    }
    if (!!userCtx.notification_Information) {
      showInfo(userCtx.notification_Information)
      userCtx.setNotificationInformation(undefined)
    }
  }, [getNotificationsCount, showError, showInfo, userCtx])

  // SignalR
  useEffect(() => {
    if (!notificationHubConnection) {
      setupSignalRConnection()
    }

    // Monitor for SignalR errors
    if (hubError) {
      console.warn(
        "SignalR Warning: ",
        hubError,
        `\nConnection State: ${hubConnectionState}`,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.notification_hubConnection, hubError])

  const getOrganizationName = (organization_id) => {
    if (_.isEmpty(organization_id) || organization_id === "0") {
      LogWarning("Organization ID is 0 or empty")
      return ""
    }

    const useOrgs = organizationsCache || userCtx.session.organizations
    const org = useOrgs.find(
      (x) =>
        x.organization_id === organization_id ||
        x.organization_alias === organization_id,
    )

    return org ? org.organization_name : "Unknown organization"
  }

  const dialogHeaderTemplate = (label, iconName) => {
    return {
      label: label,
      template: (item, options) => {
        return (
          /* custom element */
          <button
            type="button"
            className={`${noteStyle.noteMainTabHeader} ${
              options.active ? noteStyle.noteMainTabHeaderActive : ""
            }`}
            target={item.target}
            onClick={options.onClick}
          >
            <span
              className="material-icons"
              style={{ verticalAlign: "middle" }}
            >
              {iconName}
            </span>
            <span
              className={options.labelClassName}
              style={{ verticalAlign: "middle" }}
            >
              {item.label}
            </span>
          </button>
        )
      },
    }
  }

  const dialogHeader = () => {
    let menuItems = [dialogHeaderTemplate("Notifications", "notifications")]

    if (
      userCtx.session.user.staff_role !== 9 &&
      userCtx.session.user.staff_role !== 10
    ) {
      menuItems.push(dialogHeaderTemplate("Tasks", "task"))
    }

    return (
      <TabMenu
        model={menuItems}
        activeIndex={userCtx.notification_show_state === "tasks" ? 1 : 0}
        onTabChange={(e) =>
          userCtx.setNotificationShowState(
            e.index === 0 ? "notifications" : "tasks",
          )
        }
      />
    )
  }

  //Display variables:
  let icon = "notifications"
  if (mutedNotifications !== null && mutedNotifications.channelSignalR) {
    icon = "notifications_paused"
  }
  if (errorState !== null) {
    icon = "notifications_off"
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        header={dialogHeader()}
        visible={userCtx.notification_show_state !== ""}
        onHide={() => {
          userCtx.setNotificationShowState("")
        }}
        className="custom_dialog"
        style={{ width: "96vw", height: "96vh", maxWidth: "1400px" }}
      >
        {userCtx.notification_show_state === "tasks" ? (
          <TasksForm
            users={taskUsers}
            onSetUsers={setTaskUsers}
            skipUserRefresh={skipTaskUsersRefresh}
            onSetSkipUserRefresh={setSkipTaskUsersRefresh}
            activeKey={tasksActiveKey}
            activeSubKey={tasksActiveSubKey}
            onShowError={showError}
            onHideDialog={() => {
              userCtx.setNotificationShowState("")
            }}
          />
        ) : (
          <NotificationsForm
            ActiveKey={notificationsActiveKey}
            organizationList={organizationsCache}
            getOrganizationName={getOrganizationName}
            onResetActiveKey={() => {
              setNotificationsActiveKey("")
            }}
            onShowError={showError}
            onShowNotification={showInfo}
            onHandleActionURL={handleActionURL}
            onRefreshBellCounter={setRefreshCount}
            onSetMutedNotifications={setMutedNotifications}
          />
        )}
      </Dialog>

      <li
        className="top-icon"
        onClick={() => {
          handleButtonClick()
        }}
      >
        <span className="material-icons top-icon-span p-overlay-badge">
          {icon}
          {notificationsCount && notificationsCount.numTotal > 0 && (
            <Badge
              value={notificationsCount.numTotal}
              style={{ fontSize: 12, transform: "translate(80%,-40%)" }}
            />
          )}
        </span>
        <label className="top-menu-label">Notifications</label>
      </li>
    </>
  )
}
